import { SIDEBAR_CATEGORIES, type SidebarCategoryId } from './Sidebar/sidebarPalette';

export type TriggerKind = 'manual' | 'webhook' | 'scheduled' | 'event';

export type ManualVariable = { name: string; type: string };

export type ManualTriggerSettings = { variables: ManualVariable[] };
export type WebhookTriggerSettings = { path: string; auth: 'none' | 'apiKey' | 'hmac' };
export type ScheduledTriggerSettings = {
  scheduleMode: 'cron' | 'simple';
  cronExpression: string;
  simpleInterval: number;
  simpleUnit: 'hours' | 'days';
  /** HH:mm — used when simpleUnit is days */
  dailyTime: string;
};
export type EventTriggerSettings = { pattern: string };

export type TriggerSettings =
  | ({ kind: 'manual' } & ManualTriggerSettings)
  | ({ kind: 'webhook' } & WebhookTriggerSettings)
  | ({ kind: 'scheduled' } & ScheduledTriggerSettings)
  | ({ kind: 'event' } & EventTriggerSettings);

const PALETTE_TO_KIND: Record<string, TriggerKind> = {
  Trigger_Manual: 'manual',
  StartNode: 'manual',
  Trigger_Webhook: 'webhook',
  WebhookTrigger: 'webhook',
  Trigger_Scheduled: 'scheduled',
  Trigger_Event: 'event',
  Trigger_EventConnection: 'event',
};

export function paletteTypeToTriggerKind(paletteType: string): TriggerKind | null {
  return PALETTE_TO_KIND[paletteType] ?? null;
}

export function isTriggersCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.TRIGGERS;
}

export function defaultTriggerSettings(kind: TriggerKind): TriggerSettings {
  switch (kind) {
    case 'manual':
      return { kind: 'manual', variables: [] };
    case 'webhook':
      return { kind: 'webhook', path: '/api/leads', auth: 'none' };
    case 'scheduled':
      return {
        kind: 'scheduled',
        scheduleMode: 'simple',
        cronExpression: '0 9 * * *',
        simpleInterval: 1,
        simpleUnit: 'days',
        dailyTime: '09:00',
      };
    case 'event':
      return { kind: 'event', pattern: 'lead.*' };
    default:
      return { kind: 'manual', variables: [] };
  }
}

function formatDailyTimeLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return hhmm;
  const hour12 = h % 12 || 12;
  const ampm = h < 12 ? 'am' : 'pm';
  const min = Number.isNaN(m) ? 0 : m;
  return min === 0 ? `${hour12}${ampm}` : `${hour12}:${String(min).padStart(2, '0')}${ampm}`;
}

export function buildTriggerPreview(kind: TriggerKind, settings: TriggerSettings): string {
  switch (kind) {
    case 'manual':
      return 'Click to run';
    case 'webhook': {
      const s = settings as WebhookTriggerSettings & { kind: 'webhook' };
      const p = s.path.startsWith('/') ? s.path : `/${s.path}`;
      return p;
    }
    case 'scheduled': {
      const s = settings as ScheduledTriggerSettings & { kind: 'scheduled' };
      if (s.scheduleMode === 'cron' && s.cronExpression.trim()) {
        return s.cronExpression.trim();
      }
      const n = Math.max(1, s.simpleInterval);
      if (s.simpleUnit === 'hours') {
        return n === 1 ? 'Every hour' : `Every ${n} hours`;
      }
      if (n === 1) {
        return `Every day ${formatDailyTimeLabel(s.dailyTime)}`;
      }
      return `Every ${n} days ${formatDailyTimeLabel(s.dailyTime)}`;
    }
    case 'event': {
      const s = settings as EventTriggerSettings & { kind: 'event' };
      return s.pattern || '.*';
    }
    default:
      return '';
  }
}

export function webhookUrlPreview(path: string): string {
  const base =
    (typeof process !== 'undefined' && process.env.REACT_APP_API_URL_backend?.replace(/\/$/, '')) ||
    `${typeof window !== 'undefined' ? window.location.origin : ''}/webhook`;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}
